import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AutoSeo from "@/components/AutoSeo";
import HeartButton from "@/components/HeartButton";
import PageMeta from "@/components/PageMeta";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { toast } from "sonner";
import { MagnifyingGlass, Scroll, Compass, Clock, GlobeHemisphereWest } from "@phosphor-icons/react";

/**
 * Historical Recipes — public listing page.
 *
 * Filters: free-text search, country, continent, century, decade, first letter.
 * Sort: title A-Z (default), year ascending, year descending.
 * Gated on SiteSettings.historical_recipes_enabled — when off, the route
 * 404s for the public. Admin/SuperAdmin can still preview via /admin
 * curation but not through this page's normal load path.
 */

const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// Public-domain source books that back the Historical Recipes collection.
// Every cover image was downloaded from archive.org and lives in
// /public/historical-sources/. Every `archive_url` links to the exact
// scanned volume so readers can verify facts against the original page.
// Ordered by number of recipes drawn from each source (top of the list =
// most recipes).
// The "Our sources" grid is now driven by GET /api/historical/sources so
// admins can add / edit / hide books via the Admin → Historical Sources tab.
// We keep this hard-coded list as a fallback ONLY for the very first paint
// while the network round-trip lands, so the page never flashes empty.
const HISTORICAL_SOURCES_FALLBACK = [
  { slug: "beeton",    title: "Book of Household Management",           author: "Isabella Beeton",              year: 1861, country: "United Kingdom", cover_url: "/historical-sources/beeton.jpg",    archive_url: "https://archive.org/details/mrsbeetonshouse00beetuoft" },
  { slug: "farmer",    title: "Boston Cooking-School Cook Book",        author: "Fannie Merritt Farmer",        year: 1896, country: "United States",  cover_url: "/historical-sources/farmer.jpg",    archive_url: "https://archive.org/details/bostoncookingsch00farm" },
  { slug: "simmons",   title: "American Cookery",                       author: "Amelia Simmons",               year: 1796, country: "United States",  cover_url: "/historical-sources/simmons.jpg",   archive_url: "https://archive.org/details/americancookery12815gut" },
  { slug: "apicius",   title: "De Re Coquinaria",                       author: "Apicius",                      year: 1,    country: "Roman Empire",   cover_url: "/historical-sources/apicius.jpg",   archive_url: "https://archive.org/details/apicicaelidereco00apicrich" },
  { slug: "glasse",    title: "The Art of Cookery Made Plain and Easy", author: "Hannah Glasse",                year: 1747, country: "United Kingdom", cover_url: "/historical-sources/glasse.jpg",    archive_url: "https://archive.org/details/artofcookerymade00glas" },
  { slug: "lavarenne", title: "Le Cuisinier François",                  author: "François Pierre La Varenne",   year: 1651, country: "France",         cover_url: "/historical-sources/lavarenne.jpg", archive_url: "https://archive.org/details/lecuisinierfranc00lava" },
  { slug: "artusi",    title: "La Scienza in Cucina",                   author: "Pellegrino Artusi",            year: 1891, country: "Italy",          cover_url: "/historical-sources/artusi.jpg",    archive_url: "https://archive.org/details/artusi-1891" },
  { slug: "fisher",    title: "What Mrs Fisher Knows About Old Southern Cooking", author: "Abby Fisher",       year: 1881, country: "United States",  cover_url: "/historical-sources/fisher.jpg",    archive_url: "https://archive.org/details/whatmrsfisherkno00fishrich" },
  { slug: "acton",     title: "Modern Cookery for Private Families",    author: "Eliza Acton",                  year: 1845, country: "United Kingdom", cover_url: "/historical-sources/acton.jpg",     archive_url: "https://archive.org/details/moderncookeryfo00actogoog" },
];

function centuryLabel(c) {
  if (c === null || c === undefined) return "";
  if (c < 0) return `${Math.abs(c)}th C BC`;
  if (c === 1) return "1st C AD";
  if (c === 2) return "2nd C AD";
  if (c === 3) return "3rd C AD";
  return `${c}th C`;
}

export default function Historical() {
  const { flags, loading: flagsLoading } = useSiteFlags();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState({ items: [], total: 0, loading: true });
  const [facets, setFacets] = useState({ countries: [], continents: [], centuries: [], decades: [] });
  // "Our sources" grid — fetched from backend so admins can add/edit/hide books
  // via Admin → Historical Sources. Falls back to the static seed list only
  // for the very first paint while the network round-trip lands.
  const [sourceBooks, setSourceBooks] = useState(HISTORICAL_SOURCES_FALLBACK);
  const filters = useMemo(() => ({
    q: params.get("q") || "",
    country: params.get("country") || "",
    continent: params.get("continent") || "",
    century: params.get("century") || "",
    decade: params.get("decade") || "",
    letter: params.get("letter") || "",
    sort: params.get("sort") || "title_asc",
  }), [params]);

  useEffect(() => {
    if (flagsLoading) return;
    if (!flags.historical_recipes_enabled) { nav("/", { replace: true }); return; }
    (async () => {
      try {
        const f = await api.get("/historical/facets");
        setFacets(f.data);
      } catch { /* silent */ }
      try {
        const s = await api.get("/historical/sources");
        if (Array.isArray(s.data?.items) && s.data.items.length) setSourceBooks(s.data.items);
      } catch { /* silent — fall back to seed list */ }
    })();
  }, [flags.historical_recipes_enabled, nav, flagsLoading]);

  useEffect(() => {
    if (flagsLoading || !flags.historical_recipes_enabled) return;
    setState((s) => ({ ...s, loading: true, items: [] }));
    (async () => {
      try {
        const clean = {};
        Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) clean[k] = v; });
        // Backend caps at 200 per request. Fetch first page synchronously
        // then paginate through the rest in the background so the whole
        // set is available for the A-Z scrolling view. Previously we
        // fetched a single page of 60 which meant collections with more
        // than 60 titles cut off around M in alphabetical order.
        const PAGE = 200;
        const first = await api.get("/historical/recipes", { params: { ...clean, limit: PAGE, skip: 0 } });
        let items = first.data.items || [];
        const total = first.data.total || items.length;
        setState({ items, total, loading: total > items.length });
        // Follow-up pages (rare — only if the DB has >200 published
        // historical recipes). Fetched sequentially so the UI grows in
        // order and users can start scrolling straight away.
        let skip = items.length;
        while (skip < total) {
          const more = await api.get("/historical/recipes",
            { params: { ...clean, limit: PAGE, skip } });
          items = items.concat(more.data.items || []);
          skip = items.length;
          setState({ items, total, loading: skip < total });
          if (!(more.data.items || []).length) break;   // safety
        }
      } catch (e) {
        toast.error("Couldn't load historical recipes.");
        setState({ items: [], total: 0, loading: false });
      }
    })();
  }, [filters, flags.historical_recipes_enabled, flagsLoading]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  /* SEO structured data. Two schemas emitted:
   *   1. BreadcrumbList — makes Google show "Recipe Filer › Historical
   *      Recipes" under the search result URL.
   *   2. ItemList — points Google at all 50 historical recipe URLs so it
   *      discovers the individual recipe pages faster (fresh crawler
   *      hint even when the sitemap is delayed). Populated once
   *      state.items has arrived; before that a minimal ItemList is
   *      emitted so the tag exists in the crawler snapshot regardless. */
  const jsonLd = useMemo(() => {
    const items = (state.items || []).map((r, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://recipefiler.com/historical/${r.slug || r.historical_id}`,
      name: r.title,
    }));
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://recipefiler.com/" },
          { "@type": "ListItem", position: 2, name: "Historical Recipes" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Historical Recipes on Recipe Filer",
        description: "Historical recipes drawn from real public-domain cookbooks — every continent, 5000+ years of cooking.",
        numberOfItems: items.length || state.total || 50,
        itemListElement: items,
      },
    ];
  }, [state.items, state.total]);

  return (
    <div className="min-h-screen bg-rf-cream" data-testid="historical-page">
      <AutoSeo
        pageKey="historical"
        fallbackTitle="Historical Recipes — 50 real dishes from public-domain cookbooks"
        fallbackDescription="50 historical recipes drawn from real public-domain cookbooks — every continent, 5000+ years of cooking. Modernised for a modern kitchen without losing their soul."
        canonical="https://recipefiler.com/historical"
        ogImage="/historical-hero.jpg"
        jsonLd={jsonLd}
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-900/90 via-rf-ink to-rf-ink text-rf-cream py-12 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <p className="overline !text-rf-amber mb-2">Historical Recipes</p>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mb-4 leading-tight">
              Cook the past.<br />From Apicius to Fannie Farmer.
            </h1>
            <p className="text-lg opacity-90 max-w-2xl">
              Historic recipes, every continent, 5000+ years of cooking. Every recipe is
              drawn from a real public-domain cookbook — modernised so it works in a modern kitchen,
              without losing its soul.
            </p>
            <Link to="/historical/progress" className="btn-amber mt-6 inline-flex items-center gap-2" data-testid="historical-progress-link">
              <Compass size={16} weight="bold" /> Your Historical Cook progress
            </Link>
          </div>
          {/* Hero — a single 3:2 illustrated timeline showing kitchens
              from Mesopotamia (2000 BC) through to Victorian (c.1900).
              Sets the mood for the section without pretending to be any
              specific brand or book. */}
          <div className="lg:col-span-2" data-testid="historical-hero-image">
            <img
              src="/historical-hero.jpg"
              alt="A timeline of kitchens through history — from a Mesopotamian hearth (c.2000 BC) through Ancient Rome, medieval, Tudor, Georgian, and Victorian scenes."
              width={1024}
              height={687}
              loading="eager"
              fetchPriority="high"
              className="w-full aspect-[3/2] rounded-3xl object-cover shadow-2xl ring-1 ring-rf-cream/10"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <section aria-labelledby="historical-filters-heading" className="bg-white rounded-3xl p-5 mb-6 border border-rf-ink/5 shadow-sm">
          <h2 id="historical-filters-heading" className="sr-only">Filter historical recipes</h2>
          <div className="flex gap-3 flex-wrap items-center mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft" />
              <input
                data-testid="historical-search"
                type="text"
                placeholder="Search by name, ingredient, cuisine…"
                defaultValue={filters.q}
                onKeyDown={(e) => e.key === "Enter" && setFilter("q", e.target.value)}
                onBlur={(e) => setFilter("q", e.target.value)}
                className="input-rf w-full !pl-9"
              />
            </div>
            <select data-testid="historical-continent" value={filters.continent} onChange={(e) => setFilter("continent", e.target.value)} className="input-rf max-w-[200px]">
              <option value="">All continents</option>
              {facets.continents.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
            </select>
            <select data-testid="historical-country" value={filters.country} onChange={(e) => setFilter("country", e.target.value)} className="input-rf max-w-[220px]">
              <option value="">All countries</option>
              {facets.countries.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
            </select>
            <select data-testid="historical-century" value={filters.century} onChange={(e) => setFilter("century", e.target.value)} className="input-rf max-w-[180px]">
              <option value="">All centuries</option>
              {facets.centuries.map((c) => <option key={c.century} value={c.century}>{centuryLabel(c.century)} ({c.count})</option>)}
            </select>
            <select data-testid="historical-decade" value={filters.decade} onChange={(e) => setFilter("decade", e.target.value)} className="input-rf max-w-[180px]">
              <option value="">All decades</option>
              {facets.decades.map((d) => <option key={d.decade} value={d.decade}>{d.decade}s ({d.count})</option>)}
            </select>
            <select data-testid="historical-sort" value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)} className="input-rf max-w-[180px]">
              <option value="title_asc">Title A–Z</option>
              <option value="year_asc">Year (oldest first)</option>
              <option value="year_desc">Year (newest first)</option>
            </select>
          </div>

          {/* A–Z */}
          <div className="flex gap-1 flex-wrap" data-testid="historical-alphabet">
            <button onClick={() => setFilter("letter", "")} data-testid="historical-letter-all"
              className={`text-xs font-black px-3 sm:px-2.5 py-2.5 sm:py-1 rounded-full ${!filters.letter ? "bg-rf-ink text-rf-cream" : "bg-rf-cream-alt text-rf-ink-soft"}`}>
              All
            </button>
            {ALPHA.map((L) => (
              <button key={L} data-testid={`historical-letter-${L}`}
                onClick={() => setFilter("letter", L === filters.letter ? "" : L)}
                className={`text-xs font-black min-w-[40px] sm:min-w-0 px-2.5 py-2.5 sm:py-1 rounded-full ${filters.letter === L ? "bg-rf-orange text-white" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-cream"}`}>
                {L}
              </button>
            ))}
          </div>
        </section>

        {/* Results */}
        <section aria-labelledby="historical-results-heading">
          <h2 id="historical-results-heading" className="sr-only">
            {state.loading ? "Loading historical recipes" : `${state.items.length} historical recipes`}
          </h2>
          {state.loading ? (
          <p className="text-center text-rf-ink-soft py-16">Loading…</p>
        ) : state.items.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-rf-ink/5">
            <Scroll size={48} className="mx-auto text-rf-ink-soft mb-4" />
            <p className="text-rf-ink-soft">No recipes match those filters. Try widening your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="historical-grid">
            {state.items.map((r) => (
              <div key={r.historical_id} className="relative">
              <Link to={`/historical/${r.slug || r.historical_id}`}
                data-testid={`historical-card-${r.historical_id}`}
                className="block h-full bg-white rounded-3xl overflow-hidden border border-rf-ink/5 shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5">
                <div className="aspect-[4/3] bg-gradient-to-br from-amber-800 to-rf-ink relative">
                  {r.image_url ? (
                    <img
                      src={r.image_url}
                      alt={r.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center opacity-10">
                      <Scroll size={80} weight="fill" className="text-rf-cream" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 bg-rf-cream text-rf-ink text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full">
                    {r.year > 0 ? r.year : `${Math.abs(r.year)} BC`}
                  </span>
                  <span className="absolute top-3 right-3 bg-rf-orange text-white text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full">
                    {r.country}
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft mb-1">{r.era_label || centuryLabel(r.century)}</p>
                  <h3 className="font-display font-black text-lg text-rf-ink leading-tight mb-2 line-clamp-2">{r.title}</h3>
                  <p className="text-xs text-rf-ink-soft italic line-clamp-2 pr-14">from <b className="not-italic">{r.source_book}</b> — {r.author}</p>
                </div>
              </Link>
              <HeartButton kind="historical" id={r.historical_id} count={r.favourite_count} className="absolute bottom-3 right-3 z-10" />
              </div>
            ))}
          </div>
          )}
        </section>

        {/* Sources — every book we've drawn recipes from, with a live
            link to the actual scanned volume on archive.org. Reinforces
            the "100% factually correct" positioning and gives Google a
            batch of high-authority outbound links.

            Nine covers ship as static assets in /public/historical-sources/.
            The other 17+ books are in the same seed set but don't have a
            downloadable archive.org cover — they still appear in the
            recipe detail pages' source citations. */}
        <section aria-labelledby="sources-heading" className="mt-16 pt-10 border-t border-rf-ink/10" data-testid="historical-sources-section">
          <h2 id="sources-heading" className="font-display font-black text-2xl sm:text-3xl text-rf-ink mb-2">Our sources</h2>
          <p className="text-rf-ink-soft mb-6 max-w-2xl text-sm">
            Every historical recipe on Recipe Filer is drawn from a real, pre-1900 public-domain cookbook. Each source is linked to its scan on <a href="https://archive.org" target="_blank" rel="noreferrer" className="underline text-rf-orange">Internet Archive</a> so you can verify facts against the original page.
          </p>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5" data-testid="historical-sources-list">
            {sourceBooks.map((b) => (
              <li key={b.slug || b.source_id}>
                <a
                  href={b.archive_url || "#"}
                  target={b.archive_url ? "_blank" : undefined}
                  rel={b.archive_url ? "noreferrer" : undefined}
                  className="block group"
                  data-testid={`historical-source-${b.slug || "row"}`}
                >
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-rf-cream-alt shadow-md group-hover:shadow-xl transition-shadow">
                    {b.cover_url ? (
                      <img
                        src={b.cover_url}
                        alt={`${b.title} — cover scan from archive.org`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-rf-ink-soft/50 text-xs text-center px-2 italic">
                        {b.title}
                      </div>
                    )}
                  </div>
                  <p className="font-display font-extrabold text-sm text-rf-ink mt-2 leading-tight group-hover:text-rf-orange">{b.title}</p>
                  <p className="text-xs text-rf-ink-soft">
                    {b.author}
                    {b.year ? (b.year > 0 ? ` · ${b.year}` : ` · ${Math.abs(b.year)} BC`) : ""}
                  </p>
                  {b.archive_url && (
                    <p className="text-[10px] text-rf-orange font-bold uppercase tracking-widest mt-1">View on Internet Archive →</p>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
